import { get } from 'svelte/store';
import { userRoles } from '../stores/roles.js';
import { companyRole, isWriteRole } from '../stores/capabilities.js';

/**
 * Route conditions for svelte-spa-router.
 *
 * The sidebar hides what a role cannot use, but hiding a link is not a guard:
 * every route was reachable by typing its hash. A commercialista could open
 * /invoices/new, fill in a customer, the line items and the payment terms, and
 * discover on save that `companyContext()` refuses every write from that role —
 * work invited by the interface and thrown away by the server.
 *
 * These say no at navigation time instead. The server remains the authority;
 * this only stops the application from offering what the server will refuse.
 */

/** True when the signed-in user holds any of these roles. `admin` always passes. */
function hasAny(roles) {
  const held = get(userRoles) || [];
  return held.includes('admin') || roles.some((role) => held.includes(role));
}

/** Create, edit or send: the roles that may change something. */
/**
 * Pages that only make sense to someone who can write (new invoice, drafts):
 * the role in the active company decides, exactly as on the server. Only when
 * that role cannot be known (no active company yet, server not answering) does
 * the global role stand in, as it always did.
 */
export async function canWrite() {
  const role = await companyRole();
  return role === undefined ? hasAny(['operator']) : isWriteRole(role);
}

/** Administration of the system itself. */
export const isAdminOnly = () => hasAny([]);

/**
 * True when the user may only read — the state the interface has to state
 * plainly rather than let someone find out by losing a form.
 */
export function isReadOnly() {
  const held = get(userRoles) || [];
  if (held.includes('admin') || held.includes('operator')) return false;
  return held.includes('commercialista') || held.includes('readonly');
}
